import { client } from './api';
import type { FaxItemKind } from './faxClassification';

/**
 * Filing a fax from the inbox into a patient's chart, and archiving one.
 *
 * Filing puts the fax on a task of an episode as a document, on a task that
 * is already there or on a new misc task. A filed fax leaves the working list.
 * Archiving takes a fax out of the list without filing it, which is what a
 * returned order needs: it is already on its order.
 */

export interface RouteEpisodeOption {
  id: string;
  admission_id: string;
  start_date: string;
  end_date: string;
  status: string;
  /** The Patient Manager's current episode, which the picker starts on. */
  is_current: boolean;
}

export interface RouteTaskOption {
  id: string;
  task_type: string;
  display_name: string;
  task_date: string;
  status: string;
  document_count: number;
  /** False for a signed misc task or a locked chart. */
  accepts_documents: boolean;
}

export interface RouteSuggestion {
  /** Null means a new task of `misc_task_type`. */
  task_id: string | null;
  misc_task_type: string;
  hint: string;
}

export interface RouteTaskList {
  tasks: RouteTaskOption[];
  suggestion: RouteSuggestion;
  misc_task_types: string[];
  /** The episode's chart is locked, so nothing can be filed to it. */
  chart_locked?: boolean;
}

export interface RouteNewTask {
  misc_task_type: string;
  task_name?: string | null;
}

export interface RouteFaxRequest {
  patient_id: string;
  episode_id: string;
  task_id?: string | null;
  new_task?: RouteNewTask | null;
  fax_type_id?: string | null;
}

export interface RouteFaxResponse {
  task_id: string;
  task_display_name: string;
  patient_document_id: string | null;
  created_task: boolean;
  /** True when this repeated a filing that had already happened. */
  already_filed: boolean;
  chart_filed_at: string;
}

export interface FaxRoutingState {
  chart_filed_at: string | null;
  chart_task_id: string | null;
  archived_at: string | null;
}

/** Which list the inbox shows. */
export type FaxInboxView = 'working' | 'filed' | 'archived';

export async function listRouteEpisodes(
  patientId: string
): Promise<RouteEpisodeOption[]> {
  const response = await client.get<RouteEpisodeOption[], unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-routing/patients/${patientId}/episodes`,
  });
  return response.data;
}

export async function listRouteTasks(
  episodeId: string,
  faxTypeId?: string | null
): Promise<RouteTaskList> {
  const response = await client.get<RouteTaskList, unknown, true>({
    query: { fax_type_id: faxTypeId ?? undefined },
    responseType: 'json',
    throwOnError: true,
    url: `/fax-routing/episodes/${episodeId}/tasks`,
  });
  return response.data;
}

export async function routeFaxItem(
  kind: FaxItemKind,
  id: string,
  body: RouteFaxRequest
): Promise<RouteFaxResponse> {
  const response = await client.post<RouteFaxResponse, unknown, true>({
    body,
    responseType: 'json',
    throwOnError: true,
    url: `/fax-routing/${kind}/${id}/route`,
  });
  return response.data;
}

export async function archiveFaxItem(
  kind: FaxItemKind,
  id: string
): Promise<FaxRoutingState> {
  const response = await client.post<FaxRoutingState, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-routing/${kind}/${id}/archive`,
  });
  return response.data;
}

export async function unarchiveFaxItem(
  kind: FaxItemKind,
  id: string
): Promise<FaxRoutingState> {
  const response = await client.delete<FaxRoutingState, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-routing/${kind}/${id}/archive`,
  });
  return response.data;
}
